import { createSlice } from '@reduxjs/toolkit';
import {
  createTask,
  createAiPhotoshootingTaskFromGallery,
  getAvatars,
  getAiPhotoshootingTaskList,
  goToTheAiPhotoshootingTask,
  removeTask,
  sendAiPhotoshootingImageFeedback,
} from './operations';

import { TESTTASK_ID, TEST_IMAGE_ID } from 'utils/configs';
import {
  FILTER_CONFIG,
  RESULT_PARAMS_CONFIG,
} from 'utils/aiPhotoshootingConfig';

import defaultInputImage from 'media/images/test_input.jpg';
import defaultResultImage from 'media/images/test_result.jpg';

const defaultTask = {
  id: TESTTASK_ID,
  avatar: { image: '07966_00.jpeg' },
  data: [
    {
      id: TEST_IMAGE_ID,
      input_image: defaultInputImage,
      input_image_small: defaultInputImage,
      input_image_large: defaultInputImage,
      results: {
        images: [
          {
            id: TEST_IMAGE_ID,
            result_image: defaultResultImage,
            result_image_basic: defaultResultImage,
            result_image_large: defaultResultImage,
          },
        ],
      },
    },
  ],

  status: 'test',
};

let aiPhotoshootingInitialState = {
  models: [],
  taskList: [],
  taskDetails: null,
  taskStatus: null,
  filters: Object.keys(FILTER_CONFIG).reduce((acc, key) => {
    acc[key] = FILTER_CONFIG[key].defaultValue;
    return acc;
  }, {}),
  resultParams: Object.keys(RESULT_PARAMS_CONFIG).reduce((acc, key) => {
    acc[key] = RESULT_PARAMS_CONFIG[key].defaultValue;
    return acc;
  }, {}),
  isLoading: false,
  responseErrorMessage: null,
};

const handlePending = state => {
  state.responseErrorMessage = null;
  state.isLoading = true;
};

const handleRejected = (state, { payload }) => {
  state.responseErrorMessage = payload;
  state.isLoading = false;
};

export const aiPhotoshootingSlice = createSlice({
  name: 'aiphotoshooting',
  initialState: aiPhotoshootingInitialState,

  reducers: {
    setFilterValue(state, { payload }) {
      const { field, value } = payload;
      const config = FILTER_CONFIG[field];
      const currentValue = state.filters[field];
      const newValue = config?.type === 'boolean' ? !currentValue : value;

      if (currentValue !== newValue) {
        state.filters[field] = newValue;
      }
    },

    setResultParamsValue(state, { payload }) {
      payload.forEach(param => {
        const { field, value } = param;
        const config = RESULT_PARAMS_CONFIG[field];
        const currentValue = state.resultParams[field];
        const newValue = config?.type === 'boolean' ? !currentValue : value;

        if (currentValue !== newValue) {
          state.resultParams[field] = newValue;
        }

        if (
          field === 'model_type' &&
          newValue &&
          state.resultParams.views_count === null
        ) {
          state.resultParams.views_count = 1;
          return;
        } else if (
          field === 'views_count' &&
          newValue &&
          state.resultParams.model_type === null
        ) {
          state.resultParams.model_type = 'v1';
          return;
        } else if (
          field === 'video_model_type' &&
          newValue &&
          state.resultParams.video_duration === null
        ) {
          state.resultParams.video_duration = 7;
          return;
        } else if (
          field === 'video_duration' &&
          newValue &&
          state.resultParams.video_model_type === null
        ) {
          state.resultParams.video_model_type = 'v1';
          return;
        }
      });
    },

    resetFilters(state) {
      state.filters = aiPhotoshootingInitialState.filters;
    },

    resetResultParams(state) {
      state.resultParams = aiPhotoshootingInitialState.resultParams;
    },

    resetTaskDetails(state) {
      state.taskDetails = null;
      state.taskStatus = null;
    },

    resetTaskList(state) {
      state.taskList = [];
    },
  },

  extraReducers: builder => {
    builder
      .addCase(getAvatars.pending, handlePending)
      .addCase(getAvatars.rejected, handleRejected)
      .addCase(getAvatars.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.models = payload;
        state.responseErrorMessage = null;
      })
      .addCase(createTask.pending, handlePending)
      .addCase(createTask.rejected, handleRejected)
      .addCase(createTask.fulfilled, state => {
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(createAiPhotoshootingTaskFromGallery.pending, handlePending)
      .addCase(createAiPhotoshootingTaskFromGallery.rejected, handleRejected)
      .addCase(createAiPhotoshootingTaskFromGallery.fulfilled, state => {
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(getAiPhotoshootingTaskList.pending, handlePending)
      .addCase(getAiPhotoshootingTaskList.rejected, handleRejected)
      .addCase(getAiPhotoshootingTaskList.fulfilled, (state, { payload }) => {
        if (payload.length > 0) {
          state.taskList = payload;
        } else {
          state.taskList = [defaultTask];
        }
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(goToTheAiPhotoshootingTask.pending, handlePending)
      .addCase(goToTheAiPhotoshootingTask.rejected, handleRejected)
      .addCase(goToTheAiPhotoshootingTask.fulfilled, (state, { payload }) => {
        state.taskDetails = payload;
        state.taskStatus = payload.status;
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(removeTask.pending, handlePending)
      .addCase(removeTask.rejected, handleRejected)
      .addCase(removeTask.fulfilled, (state, { payload }) => {
        state.taskList = state.taskList.filter(task => task.id !== payload);
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(sendAiPhotoshootingImageFeedback.pending, handlePending)
      .addCase(sendAiPhotoshootingImageFeedback.rejected, handleRejected)
      .addCase(sendAiPhotoshootingImageFeedback.fulfilled, state => {
        state.isLoading = false;
        state.responseErrorMessage = null;
      });
  },
});

export const {
  setFilterValue,
  setResultParamsValue,
  resetFilters,
  resetResultParams,
  resetTaskDetails,
  resetTaskList,
} = aiPhotoshootingSlice.actions;
export const aiPhotoshootingReducer = aiPhotoshootingSlice.reducer;
